import { useTranslation } from 'react-i18next';
import { ScopieLogoMark } from '@/components/brand/ScopieLogo';

/**
 * The one loading page of signing in, signing up and returning from Google: the Scopie mark, a spinner and a short line, on the plain background; no
 * navigation, no layout. A status region with a visible label (it never suspends: it may show before any translation is loaded). Reduced motion: the
 * ring stands still and the text carries the state. After about 10 seconds it says so and offers a retry.
 */
export function AuthGateScreen({ slow, onRetry, stopped = false }: { slow: boolean; onRetry: () => void; stopped?: boolean }) {
  const { t } = useTranslation('common', { useSuspense: false });
  const label = t('authGate.label', 'Signing you in…');
  // the hard stop: never an endless spinner. A plain message, a retry, and a way to the sign-in page.
  if (stopped) {
    return (
      <div data-testid="auth-gate-stopped" className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-white px-6 text-center text-slate-900">
        <ScopieLogoMark size={56} />
        <p role="alert" className="text-base font-medium text-slate-700">{t('authGate.failed', 'We could not finish loading.')}</p>
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <button type="button" onClick={onRetry} className="min-h-[44px] rounded-lg bg-blue-600 px-5 text-base font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
            {t('authGate.retry', 'Try again')}
          </button>
          <a href="/login" className="inline-flex min-h-[44px] items-center rounded-lg px-5 text-base font-semibold text-blue-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600">
            {t('authGate.signIn', 'Go to sign in')}
          </a>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="auth-gate" className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-white px-6 text-center text-slate-900">
      <ScopieLogoMark size={56} />
      <div role="status" aria-live="polite" aria-label={label} className="flex flex-col items-center gap-4">
        <span aria-hidden className="h-8 w-8 rounded-full border-[3px] border-slate-200 border-t-blue-600 motion-safe:animate-spin" />
        <p className="text-base font-medium text-slate-700">{slow ? t('authGate.slow', 'This is taking longer than usual.') : label}</p>
      </div>
      {slow && (
        <button
          type="button"
          onClick={onRetry}
          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-5 text-base font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
        >
          {t('authGate.retry', 'Try again')}
        </button>
      )}
    </div>
  );
}
